import React, { useEffect, useState, useMemo } from 'react';
import { Table, Card, Button, Space, Tag, Typography, Input, Popconfirm, message, Modal, Select, Row, Col } from 'antd';
import { SearchOutlined, DeleteOutlined, EyeOutlined, CheckOutlined, CloseOutlined, ReloadOutlined, FileTextOutlined, ClockCircleOutlined, CheckCircleOutlined, GiftOutlined } from '@ant-design/icons';
import StatCard from '../components/StatCard';
import useReportStore from '../store/reportStore';

const { Title, Text } = Typography;
const { TextArea } = Input;

const statusColors = {
  pending: 'orange',
  approved: 'green',
  rejected: 'red',
};

const statusLabels = {
  pending: 'Kutilmoqda',
  approved: 'Tasdiqlangan',
  rejected: 'Rad etilgan',
};

const ReportsPage = () => {
  const { reports, loading, pagination, fetchReports, updateReportStatus, deleteReport } = useReportStore();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [actionModal, setActionModal] = useState({ open: false, id: null, status: null });
  const [adminNote, setAdminNote] = useState('');

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const handleDelete = async (id) => {
    try {
      await deleteReport(id);
      message.success("O'chirildi");
      fetchReports();
    } catch {
      message.error('Xatolik yuz berdi');
    }
  };

  const handleStatusUpdate = async () => {
    try {
      await updateReportStatus(actionModal.id, {
        status: actionModal.status,
        admin_note: adminNote,
      });
      message.success('Holat yangilandi');
      setActionModal({ open: false, id: null, status: null });
      setAdminNote('');
      fetchReports();
    } catch {
      message.error('Xatolik yuz berdi');
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60, sorter: (a, b) => a.id - b.id },
    { title: 'Xizmat nomi', dataIndex: 'service_name', key: 'service_name', ellipsis: true },
    { title: 'Manzil', dataIndex: 'address', key: 'address', ellipsis: true },
    { title: 'Telefon', dataIndex: 'phone', key: 'phone' },
    {
      title: 'Bonus',
      dataIndex: 'bonus_amount',
      key: 'bonus_amount',
      render: (v) => `${Number(v || 0).toLocaleString()} UZS`,
    },
    {
      title: "Bonus to'landi",
      dataIndex: 'bonus_paid',
      key: 'bonus_paid',
      render: (v) => v ? <Tag color="green">Ha</Tag> : <Tag color="default">Yo'q</Tag>,
    },
    {
      title: 'Holat',
      dataIndex: 'status',
      key: 'status',
      render: (s) => <Tag color={statusColors[s]}>{statusLabels[s] || s}</Tag>,
    },
    {
      title: 'Sana',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (d) => d ? new Date(d).toLocaleDateString('uz-UZ') : '-',
    },
    {
      title: 'Amallar',
      key: 'actions',
      width: 180,
      render: (_, record) => (
        <Space>
          <Button type="text" icon={<EyeOutlined />} onClick={() => { setDetailData(record); setDetailOpen(true); }} />
          {record.status === 'pending' && (
            <>
              <Button
                type="text"
                style={{ color: '#52c41a' }}
                icon={<CheckOutlined />}
                onClick={() => setActionModal({ open: true, id: record.id, status: 'approved' })}
              />
              <Button
                type="text"
                danger
                icon={<CloseOutlined />}
                onClick={() => setActionModal({ open: true, id: record.id, status: 'rejected' })}
              />
            </>
          )}
          <Popconfirm title="O'chirishni tasdiqlaysizmi?" onConfirm={() => handleDelete(record.id)}>
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const filtered = reports?.filter((r) => {
    const matchSearch = !search ||
      (r.service_name || '').toLowerCase().includes(search.toLowerCase()) ||
      (r.address || '').toLowerCase().includes(search.toLowerCase()) ||
      (r.phone || '').includes(search);
    const matchStatus = !statusFilter || r.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const stats = useMemo(() => {
    const list = reports || [];
    return {
      total: list.length,
      pending: list.filter(r => r.status === 'pending').length,
      approved: list.filter(r => r.status === 'approved').length,
      totalBonus: list.reduce((s, r) => s + Number(r.bonus_amount || 0), 0),
    };
  }, [reports]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <Title level={3} style={{ margin: 0 }}>Hisobotlar</Title>
        <Space wrap>
          <Input placeholder="Qidirish..." prefix={<SearchOutlined />} value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 200 }} allowClear />
          <Select
            placeholder="Holat"
            allowClear
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 160 }}
            options={Object.entries(statusLabels).map(([k, v]) => ({ value: k, label: v }))}
          />
          <Button icon={<ReloadOutlined />} onClick={() => fetchReports()}>Yangilash</Button>
        </Space>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} lg={6}>
          <StatCard title="Jami shikoyatlar" value={stats.total} icon={<FileTextOutlined />} color="#4f46e5" loading={loading} />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard title="Kutilmoqda" value={stats.pending} icon={<ClockCircleOutlined />} color="#fa8c16" loading={loading} />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard title="Tasdiqlangan" value={stats.approved} icon={<CheckCircleOutlined />} color="#52c41a" loading={loading} />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard title="Jami bonus" value={stats.totalBonus} suffix="UZS" icon={<GiftOutlined />} color="#13c2c2" loading={loading} />
        </Col>
      </Row>

      <Card style={{ borderRadius: 12 }}>
        <Table
          dataSource={filtered}
          columns={columns}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1000 }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `Jami: ${total}`,
          }}
          onChange={(pg) => fetchReports({ page: pg.current, per_page: pg.pageSize })}
        />
      </Card>

      <Modal title="Hisobot tafsilotlari" open={detailOpen} onCancel={() => setDetailOpen(false)} footer={null} width={650}>
        {detailData && (
          <div style={{ display: 'grid', gap: 12 }}>
            <div><strong>ID:</strong> {detailData.id}</div>
            <div><strong>Foydalanuvchi ID:</strong> {detailData.user_id}</div>
            <div><strong>Xizmat nomi:</strong> {detailData.service_name}</div>
            <div><strong>Xizmat turi ID:</strong> {detailData.service_type_id}</div>
            <div><strong>Viloyat ID:</strong> {detailData.region_id}</div>
            <div><strong>Tuman ID:</strong> {detailData.district_id}</div>
            <div><strong>Manzil:</strong> {detailData.address}</div>
            <div><strong>Telefon:</strong> {detailData.phone || '-'}</div>
            <div><strong>Koordinatalar:</strong> {detailData.latitude}, {detailData.longitude}</div>
            <div><strong>Tavsif:</strong> {detailData.description}</div>
            <div><strong>Holat:</strong> <Tag color={statusColors[detailData.status]}>{statusLabels[detailData.status] || detailData.status}</Tag></div>
            <div><strong>Bonus:</strong> {Number(detailData.bonus_amount || 0).toLocaleString()} UZS</div>
            <div><strong>Bonus to'landi:</strong> {detailData.bonus_paid ? 'Ha' : "Yo'q"}</div>
            <div><strong>Admin izohi:</strong> {detailData.admin_note || '-'}</div>
            <div><strong>Yaratilgan:</strong> {detailData.created_at ? new Date(detailData.created_at).toLocaleString('uz-UZ') : '-'}</div>
          </div>
        )}
      </Modal>

      <Modal
        title={actionModal.status === 'approved' ? 'Hisobotni tasdiqlash' : 'Hisobotni rad etish'}
        open={actionModal.open}
        onCancel={() => { setActionModal({ open: false, id: null, status: null }); setAdminNote(''); }}
        onOk={handleStatusUpdate}
        okText={actionModal.status === 'approved' ? 'Tasdiqlash' : 'Rad etish'}
        okButtonProps={{ danger: actionModal.status === 'rejected' }}
        cancelText="Bekor qilish"
      >
        <div style={{ marginBottom: 12 }}>
          <Text>Admin izohi (ixtiyoriy):</Text>
        </div>
        <TextArea
          rows={3}
          value={adminNote}
          onChange={(e) => setAdminNote(e.target.value)}
          placeholder="Izoh yozing..."
        />
      </Modal>
    </div>
  );
};

export default ReportsPage;
